/**
 * voice-v8 — aus HTML lesbaren Text machen. (16.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Ausserdem moechte der Kunde haben, dass man Webseiten
 * analysieren lassen kann."*
 *
 * ## Warum nicht eine Bibliothek
 *
 * Nachgesehen: KEINE der ueblichen liegt im Baum — weder cheerio noch jsdom,
 * node-html-parser, turndown, html-to-text, linkedom, parse5 noch readability.
 * `happy-dom` ist da, aber als devDependency: zur Laufzeit nicht verfuegbar.
 * Eine neue Abhaengigkeit fuer eine Aufgabe, die ein paar Ersetzungen loesen,
 * waere teuer bezahlt.
 *
 * ## Was im Baum schon da war — und warum es nicht reichte
 *
 * VIER wortgleiche `stripHtml`-Kopien (tools/web_search.ts:705,
 * tools/research_directory.ts:430, tools/amazon_search.ts:95,
 * tools/web_image_search.ts:142). Sie sind fuer Such-Schnipsel gebaut:
 * sie entfernen `<script>`/`<style>` NICHT und plaetten mit `\s+` jede
 * Struktur. Auf eine ganze Seite angewandt ergeben sie eine Textwueste, in
 * der JavaScript-Quelltext zwischen den Absaetzen steht.
 *
 * Die brauchbare Fassung lag uneexportiert mitten in der Gmail-Zerlegung
 * (tools/google_workspace.ts). Sie ist die Vorlage hier — ergaenzt um das,
 * was eine Webseite von einer Mail unterscheidet: Navigation, Kopf- und
 * Fusszeilen, Ueberschriften, Listen, und Entities als Zahl.
 *
 * ⚠️ Das ist bewusst KEIN HTML-Parser. Es verarbeitet gutwilliges Markup.
 *    Wer daraus Sicherheitsgarantien ableitet, irrt: der Text geht an ein
 *    Modell, und fremder Text ist fremder Text (siehe
 *    server/injektions-schutz.ts, wo `read_url` deshalb als
 *    Fremdtext-Werkzeug gefuehrt wird).
 */

/** Blöcke, deren INHALT nichts zum Lesen beiträgt. */
const RAUS_MIT_INHALT = [
  "script",
  "style",
  "noscript",
  "template",
  "svg",
  "head",
  "nav",
  "header",
  "footer",
  "aside",
  "form",
  "iframe",
];

/** Benannte Entities, die in echtem Text vorkommen. */
const ENTITIES: Record<string, string> = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  laquo: "«",
  raquo: "»",
  bdquo: "„",
  ldquo: "“",
  rdquo: "”",
  sbquo: "‚",
  lsquo: "‘",
  rsquo: "’",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  auml: "ä",
  ouml: "ö",
  uuml: "ü",
  Auml: "Ä",
  Ouml: "Ö",
  Uuml: "Ü",
  szlig: "ß",
  euro: "€",
  copy: "©",
  reg: "®",
  trade: "™",
  deg: "°",
  middot: "·",
  bull: "•",
  shy: "",
};

/**
 * Entities aufloesen — benannte UND numerische.
 *
 * ⚠️ Die vier alten Kopien kannten nur sechs benannte und KEINE numerischen.
 *    Auf deutschen Seiten steht `&#228;` fuer „ä" haeufiger als `&auml;`;
 *    ohne diese Zeile las der Text sich wie ein Fehlerprotokoll.
 */
export function entitiesAufloesen(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);/gi, (_t, hex: string) =>
      sicheresZeichen(parseInt(hex, 16)),
    )
    .replace(/&#(\d+);/g, (_t, dez: string) =>
      sicheresZeichen(parseInt(dez, 10)),
    )
    .replace(
      /&([a-z]+);/gi,
      (treffer: string, name: string) => ENTITIES[name] ?? treffer,
    );
}

function sicheresZeichen(code: number): string {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return "";
  try {
    return String.fromCodePoint(code);
  } catch {
    return "";
  }
}

/** Der Titel der Seite, falls einer dasteht. */
export function titelAusHtml(html: string): string {
  const t = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html);
  if (!t?.[1]) return "";
  return entitiesAufloesen(t[1].replace(/<[^>]+>/g, ""))
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

/**
 * HTML zu lesbarem Text.
 *
 * Absätze, Überschriften und Listenpunkte bleiben als Zeilen erhalten —
 * ein Fließtext ohne Struktur ist für ein Modell deutlich schwerer zu
 * ordnen, und die Gliederung IST oft die Information.
 */
export function htmlZuText(html: string): string {
  if (!html) return "";
  let s = html;

  // 1. Kommentare und alles, was nur Maschinen angeht.
  s = s.replace(/<!--[\s\S]*?-->/g, "");
  for (const tag of RAUS_MIT_INHALT) {
    s = s.replace(
      new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}\\s*>`, "gi"),
      " ",
    );
    // Selbstschliessend oder ohne Gegenstueck.
    s = s.replace(new RegExp(`<${tag}\\b[^>]*\\/?>`, "gi"), " ");
  }

  // 2. Struktur in Zeilen uebersetzen, BEVOR die Tags fallen.
  s = s
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|section|article|tr|h[1-6]|blockquote|pre)\s*>/gi, "\n\n")
    .replace(/<li\b[^>]*>/gi, "\n• ")
    .replace(/<\/(td|th)\s*>/gi, "\t");

  // 3. Alles Uebrige entfernen.
  s = s.replace(/<[^>]+>/g, " ");

  // 4. Entities, dann aufraeumen.
  s = entitiesAufloesen(s);
  s = s
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/[^\S\n]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/(?:^|\n)[ \t]*•[ \t]*(?=\n|$)/g, "\n")
    /*
     * ⚠️ 16.09.2026, an einem ECHTEN Abruf aufgefallen — kein gefaelschter
     * Test haette das gezeigt. vespera.at lieferte:
     *
     *     „Sie lernt dich aus deinem Tag ."
     *
     * Ein entferntes Inline-Tag (<span>, <em>, <a>) hinterlaesst ein
     * Leerzeichen, und wenn dahinter ein Satzzeichen steht, haengt es in
     * der Luft. Das liest sich falsch — und ein Modell, das daraus zitiert,
     * zitiert den Fehler mit.
     */
    .replace(/[ \t]+([.,;:!?…)\]}»“”])/g, "$1")
    .replace(/([([{«„“])[ \t]+/g, "$1");

  return s.trim();
}

/**
 * Zu langen Text am SATZENDE kappen, nicht mitten im Wort.
 *
 * ⚠️ Am 04.09.2026 belegt: ein Text, der mitten im Wort endet, bringt das
 *    Modell dazu, den Abbruch zu MELDEN statt weiterzuarbeiten. Es hält den
 *    Schnitt für einen Fehler — zu Recht, denn so sieht ein Fehler aus.
 */
export function kuerzeAufSatz(text: string, maxZeichen: number): string {
  if (text.length <= maxZeichen) return text;
  const stueck = text.slice(0, maxZeichen);
  // Ein Satzende im letzten Fuenftel gilt als guter Schnitt; weiter zurueck
  // zu suchen würde mehr wegwerfen, als der saubere Schnitt wert ist.
  const fenster = Math.floor(maxZeichen * 0.2);
  const ab = maxZeichen - fenster;
  let schnitt = -1;
  for (const zeichen of [".", "!", "?", "\n"]) {
    schnitt = Math.max(schnitt, stueck.lastIndexOf(zeichen, maxZeichen));
  }
  if (schnitt >= ab) return stueck.slice(0, schnitt + 1).trim();
  const luecke = stueck.lastIndexOf(" ");
  return (luecke > ab ? stueck.slice(0, luecke) : stueck).trim();
}

/**
 * Die Lauf-Marker aus einem Auftragsergebnis nehmen. (18.09.2026)
 *
 * `server/scheduled-jobs-runner.ts` laesst einen geplanten Auftrag mit
 * `TREFFER: ` bzw. `NOCH-NICHTS: ` beginnen, damit der Server erkennt, ob
 * etwas gefunden wurde. Das ist eine Absprache zwischen zwei Maschinen —
 * der Server liest den Marker und liess ihn dann stehen.
 *
 * ⚠️ Martins Foto vom 18.09. zeigt ihn woertlich in der Glocke:
 *    *„TREFFER: Die Log-Zeile ‚tts-gap via=error' haeuft sich …"*. Fuer den
 *    Menschen ist das Rauschen; ob es ein Treffer war, sieht er am Eintrag
 *    selbst. Der Marker wird gelesen, aber nicht gezeigt.
 *
 * 06.10.2026: auch `ERLEDIGT: ` — der Marker der handelnden Aufträge
 * (scheduled-jobs-runner, „beginne mit ERLEDIGT“). Er stand am Bestand in der
 * Routine der Glocke („ERLEDIGT: Es gibt keine Neuigkeiten …“). `FEHLER: `
 * bleibt stehen: das ist für den Menschen eine Auskunft, kein Rauschen.
 */
export function ohneLaufMarker(text: string): string {
  return text.replace(/^\s*(TREFFER|NOCH-NICHTS|ERLEDIGT)\s*:\s*/i, "");
}

/**
 * Auf eine Satzgrenze kuerzen — fuer MENSCHEN, mit sichtbarem Auslassungszeichen.
 *
 * ## Warum das neben kuerzeAufSatz steht und nicht statt ihr
 *
 * Es sind zwei verschiedene Fragen, die nur zufaellig gleich aussehen:
 *
 * | Wer liest? | Was ist richtig? |
 * |---|---|
 * | das Modell (`kuerzeAufSatz`) | KEIN Zeichen — ein „…" laesst es den Abbruch melden statt weiterzuarbeiten (belegt 04.09.2026) |
 * | ein Mensch (hier) | ein „…" — sonst sieht er nicht, dass mehr da ist |
 *
 * ⚠️ Diese Fassung lag bis zum 18.09.2026 als KOPIE in server/panel-wache.ts.
 *    Zwei Funktionen gleichen Namens mit unterschiedlichem Verhalten in einem
 *    Baum sind genau das Muster, das hier schon mehrfach Schaden gemacht hat.
 */
export function kuerzeFuerAnzeige(text: string, max: number): string {
  const t = text.trim();
  if (max <= 0) return "";
  if (t.length <= max) return t;
  const stueck = t.slice(0, max);
  const punkt = Math.max(
    stueck.lastIndexOf(". "),
    stueck.lastIndexOf("! "),
    stueck.lastIndexOf("? "),
  );
  if (punkt > max * 0.4) return stueck.slice(0, punkt + 1);
  return stueck.slice(0, Math.max(0, max - 1)).trimEnd() + "…";
}

/**
 * Das Vorschaubild einer Seite — `og:image`, sonst `twitter:image`. (18.09.2026)
 *
 * Martin, zur Karte „Webseite gelesen": *„da sollte das Bild von der Seite
 * sein, daneben der Text! Weisst du was ich meine?!"* — genau das, was jede
 * Chat-App beim Einfuegen eines Links zeigt. Die Seiten liefern es selbst,
 * wir haben nur nie danach gesehen.
 *
 * Gibt eine ABSOLUTE Adresse zurueck oder einen leeren Text. Relative Angaben
 * werden an der Seiten-Adresse aufgeloest — `og:image` darf laut Norm absolut
 * sein, ist es in der Praxis aber oft nicht.
 *
 * ⚠️ Die zurueckgegebene Adresse ist UNGEPRUEFT: sie stammt aus einer fremden
 *    Seite. Sie wird nie direkt geladen, sondern immer ueber /api/img — der
 *    Proxy loest den Namen auf und sperrt interne Ziele (server/image-proxy.ts).
 */
export function vorschaubildAusHtml(html: string, seite: string): string {
  const muster = [
    /<meta[^>]+property=["']og:image(?::secure_url)?["'][^>]+content=["']([^"']+)["']/i,
    /<meta[^>]+content=["']([^"']+)["'][^>]+property=["']og:image["']/i,
    /<meta[^>]+name=["']twitter:image["'][^>]+content=["']([^"']+)["']/i,
  ];
  for (const m of muster) {
    const treffer = m.exec(html);
    const roh = treffer?.[1]?.trim();
    if (!roh) continue;
    try {
      const u = new URL(entitiesAufloesen(roh), seite);
      if (u.protocol !== "http:" && u.protocol !== "https:") continue;
      return u.toString();
    } catch {
      continue;
    }
  }
  return "";
}
